import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Avatar, Button, Card, Chip, Table } from '@heroui/react'
import { api } from '../../lib/api'
import { latestRead } from '../../lib/latestRead'
import { PAGE_SIZES } from '../../lib/paging'
import { explain } from '../../lib/staffErrors'
import type { ReportView } from '../../lib/types'
import { Icon } from '../../ui/icons'
import { Pager } from '../../ui/Pager'
import { useStaffText } from '../../ui/StaffText'
import { ActionMenu, Empty, Find, Notice, PageHead, Pick, type MenuEntry } from '../kit'
import { Schedules } from '../Schedules'
import '../../styles/insight.css'
import { BUILT_INS, builtInPath, nameForCopy, type BuiltIn } from './builtins'
import { fullDate, initials, relativeTime } from './layout'
import { DeleteDialog, DetailsDialog } from './ReportDialogs'
import type { ReportsCtx } from './ReportsPage'

type Sort = 'updated' | 'created' | 'name'
type Line = { builtIn: BuiltIn } | { view: ReportView }

const SIZE_KEY = 'qms-reports-page-size'

function readSize(): number {
  try {
    const n = Number(localStorage.getItem(SIZE_KEY))
    return (PAGE_SIZES as readonly number[]).includes(n) ? n : PAGE_SIZES[0]
  } catch {
    return PAGE_SIZES[0]
  }
}

/** The built-ins shown on a page: they follow the organisation's own `count` reports. */
export function builtInSlice(offset: number, size: number, count: number): [number, number] {
  return [Math.max(0, offset - count), Math.max(0, offset + size - count)]
}

/** The Reports list: the organisation's own reports, then the built-in ones. */
export function ReportsList({ ctx }: { ctx: ReportsCtx }) {
  const { lang, say } = useStaffText()
  const [search, setSearch] = useState('')
  const q = useDeferredValue(search.trim())
  const [sort, setSort] = useState<Sort>('updated')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(readSize)
  const [rows, setRows] = useState<ReportView[] | null>(null)
  const [count, setCount] = useState(0)
  const [error, setError] = useState('')
  const [reload, setReload] = useState(0)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<ReportView | null>(null)
  const [deleting, setDeleting] = useState<ReportView | null>(null)
  const [mail, setMail] = useState(false)

  const builtIns = useMemo(() => {
    const words = q.toLocaleLowerCase()
    return BUILT_INS.filter((b) => !words || `${say(b.name)} ${say(b.about)}`.toLocaleLowerCase().includes(words))
  }, [q, say])
  const offset = (page - 1) * size
  const ownersOrg = ctx.identity.staff.owner
  const ready = !ownersOrg || !!ctx.organisation

  useEffect(() => {
    if (!ready) return
    return latestRead((signal) => api.pivotReports({ q, sort, page, pageSize: size, organisation: ctx.organisation }, signal), {
      done: (answer) => {
        setRows(answer.views)
        setCount(answer.count)
        setError('')
      },
      failed: (err) => setError(explain(err, lang, 'common.unreachable')),
    })
  }, [q, sort, page, size, ready, reload, ctx.organisation, lang])

  const total = builtIns.length + count
  const [biFrom, biTo] = builtInSlice(offset, size, count)
  const lines: Line[] = [...(rows ?? []).map((v) => ({ view: v })), ...(rows ? builtIns.slice(biFrom, biTo) : []).map((b) => ({ builtIn: b }))]
  const pagesCount = Math.max(1, Math.ceil(total / size))
  if (page > pagesCount && rows) setPage(pagesCount)

  const open = (line: Line) => ctx.go('builtIn' in line ? builtInPath(line.builtIn) : `/reports/${line.view.id}`)

  const duplicate = async (line: Line) => {
    try {
      const { view } =
        'builtIn' in line
          ? await api.saveReportView({ name: await nameForCopy(say(line.builtIn.name), say, ctx.organisation), kind: 'pivot', shared: true, description: say(line.builtIn.about), spec: { period: '', pivot: line.builtIn.spec! }, organisation: ctx.organisation })
          : await api.duplicateReportView(line.view.id)
      ctx.toast(say('pivot.duplicated'))
      ctx.go(`/reports/${view.id}`)
    } catch (err) {
      ctx.toast(explain(err, lang, 'common.unreachable'), 'error')
    }
  }

  const takeOver = async (view: ReportView) => {
    try {
      await api.takeOverReportView(view.id)
      ctx.toast(say('pivot.yoursNow'))
      setReload((n) => n + 1)
    } catch (err) {
      ctx.toast(explain(err, lang, 'common.unreachable'), 'error')
    }
  }

  const actionsFor = (line: Line): MenuEntry[] => {
    const v = 'view' in line ? line.view : null
    const canCopy = !('builtIn' in line) || !!line.builtIn.spec
    return [
      { id: 'open', label: say('pivot.open'), icon: 'arrowUpRight', run: () => open(line) },
      ...(v?.mine ? [{ id: 'edit', label: say('pivot.edit'), icon: 'edit' as const, run: () => setEditing(v) }] : []),
      ...(canCopy ? [{ id: 'copy', label: say('pivot.duplicate'), icon: 'copy' as const, run: () => void duplicate(line) }] : []),
      ...(v?.mayTakeOver ? [{ id: 'take', label: say('pivot.takeOver'), icon: 'user' as const, run: () => void takeOver(v) }] : []),
      ...(v?.mayDelete ? ['rule' as const, { id: 'delete', label: say('common.delete'), icon: 'trash' as const, danger: true, run: () => setDeleting(v) }] : []),
    ]
  }

  return (
    <main className="page rp-page">
      <PageHead
        title={say('entry.app.reports')}
        actions={
          <>
            <Button variant="secondary" size="sm" onPress={() => setMail(true)}>
              <Icon name="mail" size={16} />
              {say('pivot.schedules')}
            </Button>
            <Button variant="primary" size="sm" onPress={() => setCreating(true)}>
              <Icon name="plus" size={16} />
              {say('pivot.newReport')}
            </Button>
          </>
        }
      />

      <Card className="rp-list">
        <Card.Header className="rp-list__head">
          <div className="rp-list__title">
            <Card.Title>{say('pivot.allReports')}</Card.Title>
            <span>{say(total === 1 ? 'pivot.oneReport' : 'pivot.nReports', { n: total })}</span>
          </div>
          <div className="rp-list__tools">
            <Find
              fullWidth
              className="rp-grow"
              label={say('pivot.searchReports')}
              value={search}
              onChange={(v) => {
                setSearch(v)
                setPage(1)
              }}
            />
            <Pick
              className="rp-list__sort"
              label={say('pivot.sort')}
              value={sort}
              onChange={(v) => {
                setSort(v as Sort)
                setPage(1)
              }}
              options={[
                { value: 'updated', label: say('pivot.sortUpdated') },
                { value: 'created', label: say('pivot.sortCreated') },
                { value: 'name', label: say('pivot.sortName') },
              ]}
            />
          </div>
        </Card.Header>

        {error && <Notice>{error}</Notice>}

        <Table variant="secondary" className="rp-table">
          <Table.ScrollContainer>
            <Table.Content aria-label={say('pivot.allReports')}>
              <Table.Header>
                <Table.Column className="rp-col-no">{say('pivot.no')}</Table.Column>
                <Table.Column isRowHeader>{say('pivot.name')}</Table.Column>
                <Table.Column>{say('pivot.createdBy')}</Table.Column>
                <Table.Column>{say('pivot.created')}</Table.Column>
                <Table.Column>{say('pivot.updated')}</Table.Column>
                <Table.Column className="rp-col-more">
                  <span className="sr">{say('pivot.actions')}</span>
                </Table.Column>
              </Table.Header>
              <Table.Body renderEmptyState={() => (rows === null ? null : <Empty title={say(q ? 'pivot.noMatch' : 'pivot.noReports')} />)}>
                {lines.map((line, i) => {
                  const b = 'builtIn' in line ? line.builtIn : null
                  const v = 'view' in line ? line.view : null
                  const name = b ? say(b.name) : v!.name
                  const owner = b ? say('pivot.qmeter') : v!.mine ? say('pivot.you') : v!.ownerName || say('pivot.formerStaff')
                  const id = b ? `b-${b.id}` : v!.id
                  return (
                    <Table.Row key={id} id={id} className="rp-row" onAction={() => open(line)}>
                      <Table.Cell className="rp-col-no rp-num">{offset + i + 1}</Table.Cell>
                      <Table.Cell>
                        <div className="rp-name">
                          <a
                            href={b ? builtInPath(b) : `/reports/${v!.id}`}
                            title={b ? say(b.about) : undefined}
                            // The row opens the report too; the link alone answers its own press.
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.preventDefault()
                              open(line)
                            }}
                          >
                            {name}
                          </a>
                          {b && (
                            <Chip size="sm" variant="soft">
                              {say('pivot.builtIn')}
                            </Chip>
                          )}
                        </div>
                        {v && <div className="rp-sub">{[`#${v.number ?? ''}`, v.description].filter(Boolean).join(' · ')}</div>}
                      </Table.Cell>
                      <Table.Cell>
                        <span className="rp-who">
                          <Avatar size="sm" variant="soft" color={b ? 'accent' : 'default'}>
                            <Avatar.Fallback>{b ? 'Q' : initials(owner)}</Avatar.Fallback>
                          </Avatar>
                          {owner}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="rp-num rp-date">{v ? fullDate(v.createdAt) : '—'}</Table.Cell>
                      <Table.Cell className="rp-num rp-date">
                        <span title={v ? fullDate(v.updatedAt) : undefined}>{v ? relativeTime(v.updatedAt) : '—'}</span>
                      </Table.Cell>
                      <Table.Cell className="rp-col-more">
                        <ActionMenu label={say('pivot.actionsFor', { name })} icon="more" iconOnly variant="ghost" items={actionsFor(line)} />
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>

        <Pager
          total={total}
          page={page - 1}
          size={size}
          onPage={(p) => setPage(p + 1)}
          onSize={(n) => {
            setSize(n)
            setPage(1)
            try {
              localStorage.setItem(SIZE_KEY, String(n))
            } catch {
              // A private window: kept for this visit only.
            }
          }}
        />
      </Card>

      {creating && (
        <DetailsDialog
          report={null}
          organisation={ctx.organisation}
          onClose={() => setCreating(false)}
          onSaved={(view) => {
            setCreating(false)
            markFresh(view.id)
            ctx.go(`/reports/${view.id}`)
          }}
        />
      )}
      {editing && (
        <DetailsDialog
          report={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            ctx.toast(say('pivot.saved'))
            setReload((n) => n + 1)
          }}
        />
      )}
      {deleting && (
        <DeleteDialog
          report={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null)
            ctx.toast(say('pivot.deleted'))
            setReload((n) => n + 1)
          }}
        />
      )}
      {mail && <MailPanel ctx={ctx} onClose={() => setMail(false)} />}
    </main>
  )
}

/** Every report letter the reader may manage (the summary, Tickets and saved reports), in a modal. */
export function MailPanel({ ctx, branchId, onClose }: { ctx: ReportsCtx; branchId?: string; onClose: () => void }) {
  const branches = ctx.branches ?? []
  return <Schedules branches={branches} branchId={branchId || branches[0]?.id || ''} toast={ctx.toast} onClose={onClose} />
}

const FRESH_KEY = 'qms-report-fresh'

/** A report just created opens waiting for Run. */
export function markFresh(id: string) {
  try {
    sessionStorage.setItem(FRESH_KEY, id)
  } catch {
    // Without storage it simply runs at once.
  }
}

export function takeFresh(id: string): boolean {
  try {
    if (sessionStorage.getItem(FRESH_KEY) !== id) return false
    sessionStorage.removeItem(FRESH_KEY)
    return true
  } catch {
    return false
  }
}
